import { useEffect, useState } from "react";
import { api, type GlossaryEntry } from "@/lib/api";

export function Glossary() {
  const [data, setData] = useState<GlossaryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<GlossaryEntry | null>(null);
  const [search, setSearch] = useState("");

  async function load() {
    setLoading(true);
    try {
      const r = await api.get<{ data: GlossaryEntry[] }>("/api/glossary");
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const filtered = data.filter((g) => g.term.toLowerCase().includes(search.toLowerCase()));

  async function remove(id: number) {
    if (!confirm("Term wirklich löschen?")) return;
    await api.delete(`/api/glossary/${id}`);
    await load();
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">Glossar</h2>
          <p className="text-sm text-muted-foreground">
            Marken/Chemie/Normen: nie übersetzen, oder fixe Übersetzung pro Locale.
          </p>
        </div>
        <button
          type="button"
          onClick={() =>
            setEditing({
              id: 0,
              term: "",
              never_translate: true,
              fixed_translations: null,
              notes: "",
              updated_by: null,
              updated_at: "",
            })
          }
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <i className="fa-solid fa-plus" />
          Neuer Term
        </button>
      </div>
      <input
        type="search"
        placeholder="Suchen…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full max-w-xs mb-4 rounded-md border border-input bg-background px-3 py-1.5 text-sm"
      />
      {loading ? (
        <div className="text-muted-foreground text-sm py-6">Lädt…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2 pr-3 font-medium">Term</th>
                <th className="py-2 pr-3 font-medium">Verhalten</th>
                <th className="py-2 pr-3 font-medium">Fixed Translations</th>
                <th className="py-2 pr-3 font-medium">Notizen</th>
                <th className="py-2 pr-3 font-medium">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g) => (
                <tr key={g.id} className="border-b border-border/40 hover:bg-secondary/30">
                  <td className="py-2 pr-3 font-medium">{g.term}</td>
                  <td className="py-2 pr-3">
                    {g.never_translate ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">
                        nie übersetzen
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-xs">
                        fixed pro Locale
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground font-mono">
                    {g.fixed_translations ? JSON.stringify(g.fixed_translations) : "—"}
                  </td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{g.notes ?? "—"}</td>
                  <td className="py-2 pr-3">
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing(g)}
                        className="px-2 py-1 rounded text-xs hover:bg-secondary"
                      >
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void remove(g.id)}
                        className="px-2 py-1 rounded text-xs hover:bg-destructive/10 text-destructive"
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <GlossaryModal
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function GlossaryModal({
  entry,
  onClose,
  onSaved,
}: {
  entry: GlossaryEntry;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = entry.id === 0;
  const [term, setTerm] = useState(entry.term);
  const [neverTrans, setNeverTrans] = useState(entry.never_translate);
  const [fixedJson, setFixedJson] = useState(
    entry.fixed_translations ? JSON.stringify(entry.fixed_translations, null, 2) : "",
  );
  const [notes, setNotes] = useState(entry.notes ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      let fixed: Record<string, string> | null = null;
      if (!neverTrans && fixedJson.trim()) {
        try {
          fixed = JSON.parse(fixedJson) as Record<string, string>;
        } catch {
          setError("Fixed-Translations ist kein gültiges JSON");
          return;
        }
      }
      const body = { term, never_translate: neverTrans, fixed_translations: fixed, notes };
      if (isNew) {
        await api.post("/api/glossary", body);
      } else {
        await api.patch(`/api/glossary/${entry.id}`, body);
      }
      onSaved();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-[min(540px,90vw)] rounded-xl border border-border bg-card p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-semibold mb-4">{isNew ? "Neuer Term" : `Term: ${entry.term}`}</h3>
        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Term</label>
            <input
              type="text"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5"
              placeholder="z. B. BioBasic oder UEG"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="never"
              checked={neverTrans}
              onChange={(e) => setNeverTrans(e.target.checked)}
            />
            <label htmlFor="never">Nie übersetzen (Marken, Produktnamen, Chemie)</label>
          </div>
          {!neverTrans && (
            <div>
              <label className="block text-xs text-muted-foreground mb-1">
                Fixed Translations (JSON, z. B. <code>{"{ \"en\": \"LEL\", \"fr\": \"LIE\" }"}</code>)
              </label>
              <textarea
                value={fixedJson}
                onChange={(e) => setFixedJson(e.target.value)}
                rows={4}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 font-mono text-xs"
              />
            </div>
          )}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Notizen</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5"
            />
          </div>
          {error && <p className="text-destructive text-xs">{error}</p>}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-sm bg-secondary hover:bg-secondary/80"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={busy || !term}
            className="px-3 py-1.5 rounded-md text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {busy ? "speichert…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
